import { DateField, NumberField, TextField } from '@refinedev/antd';
import { useOne } from '@refinedev/core';
import { Flex, Form, FormProps, InputNumber } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import LinkTextField from '@/components/general/LinkTextField';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { ProductStatusStrings } from '@/model/Logistic/PackageItem/ProductStatus';
import { PRODUCT_TYPES, ProductTypeStrings } from '@/model/Logistic/PackageItem/ProductTypes';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { ShippingItemReferenceString } from '@/model/Logistic/Shipment/ShipmentReference';

import usePackageItemForm from '../PackageItemForm/usePackageItemForm';

interface ShipmentPackageItemShowProps {
  formProps: FormProps;
  editingPackageItem: PackageItem;
  editEnabled: boolean;
}

const ShipmentPackageItemShow = (props: ShipmentPackageItemShowProps) => {
  const { t } = useTranslation();
  const { formProps, editingPackageItem, editEnabled } = props;
  const {
    item_shipped_state,
    in_warranty_period,
    item_shipped_reference,
    warranty_start_date,
    requested_amount,
    execute_amount,
    package: itemPackage,
    product,
    unidad,
    device,
    product_type,
  } = editingPackageItem;
  // Cantidades
  const { productType, serialNumbersSelectProps } = usePackageItemForm(
    formProps,
    editingPackageItem,
    editEnabled,
  );
  const { result: productResult } = useOne({
    resource: 'elements',
    id: typeof product === 'object' ? product.id : product,
  });
  const { result: serializableProductResult } = useOne({
    resource: 'elements',
    id: unidad ?? device,
    queryOptions: {
      enabled:
        (productType === PRODUCT_TYPES.UNIT || productType === PRODUCT_TYPES.DEVICE) &&
        !editEnabled &&
        (!!unidad || !!device),
    },
    meta: { serializable: true, type: product_type },
  });
  const elementFormName = useMemo(() => {
    if (!editEnabled) return undefined;
    if (productType === PRODUCT_TYPES.UNIT) return 'unidad';
    if (productType === PRODUCT_TYPES.DEVICE) return 'device';
  }, [productType, editEnabled]);
  const elementFormPlaceholder = useMemo(() => {
    if (productType === PRODUCT_TYPES.PRODUCT) return 'Seleccione un producto';
    if (productType === PRODUCT_TYPES.UNIT) return 'Seleccione una unidad';
    if (productType === PRODUCT_TYPES.DEVICE) return 'Seleccione un dispositivo';

    return 'Seleccione un tipo de contenido';
  }, [productType]);
  // Estado del paquete
  const packageStatus = useMemo(
    () =>
      editingPackageItem && typeof editingPackageItem.package != 'number'
        ? editingPackageItem.package?.status
        : undefined,
    [editingPackageItem],
  );

  return (
    <>
      <fieldset>
        <legend>{t('Información del producto')}</legend>
        <Form.Item
          label={t('Tipo de contenido')}
          name={'product_type'}
          getValueProps={value => ({ value: t(ProductTypeStrings[value]) })}>
          <TextField value={t(ProductTypeStrings[productType])} />
        </Form.Item>

        <Form.Item label={t('Producto')}>
          <TextField value={t(productResult?.name)} />
        </Form.Item>

        {(productType === PRODUCT_TYPES.UNIT || productType === PRODUCT_TYPES.DEVICE) && (
          <Form.Item label={t('Número de serie')} name={elementFormName}>
            {editEnabled ? (
              <AntdSelect
                {...serialNumbersSelectProps}
                placeholder={elementFormPlaceholder}
                allowClear
                showSearch
              />
            ) : (
              <TextField value={serializableProductResult?.name ?? '-'} />
            )}
          </Form.Item>
        )}
        <Form.Item label={t('Estado')}>
          <TextField value={t(ProductStatusStrings[item_shipped_state] ?? '-')} />
        </Form.Item>
      </fieldset>
      <fieldset>
        <legend>{t('Información del envío')}</legend>
        <Form.Item label={t('Cantidad solicitada')}>
          <NumberField value={requested_amount} />
        </Form.Item>
        {packageStatus != PACKAGE_STATES.PENDIENTE && (
          <Form.Item
            label={t('Cantidad enviada')}
            name={editEnabled ? 'execute_amount' : undefined}>
            {editEnabled ? (
              <InputNumber style={{ width: '100%' }} />
            ) : (
              <NumberField value={execute_amount} />
            )}
          </Form.Item>
        )}
        <Form.Item label={t('Referencia de envío')}>
          <TextField value={t(ShippingItemReferenceString[item_shipped_reference] ?? '-')} />
        </Form.Item>

        {in_warranty_period != undefined && (
          <Form.Item label={t('En período de garantía')}>
            <TextField value={in_warranty_period ? t('Sí') : t('No')} />
          </Form.Item>
        )}
        {in_warranty_period && (
          <Flex vertical style={{ marginBottom: 24 }}>
            <Form.Item style={{ marginBottom: 0 }} label={<>{t('Fecha de inicio de garantía')}</>}>
              {warranty_start_date ? <DateField value={warranty_start_date} /> : '-'}
            </Form.Item>
            {typeof itemPackage != 'number' && itemPackage?.movil_link && (
              <LinkTextField label={t('Link a móvil')} to={itemPackage.movil_link} external />
            )}
          </Flex>
        )}
      </fieldset>
    </>
  );
};

export default ShipmentPackageItemShow;
